import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import type { ReactNode } from 'react'
import Cookies from 'js-cookie'
// Dynamic import to keep @sentry/react out of the main bundle
const setUserContext = (user: { id: string; email?: string } | null) => {
  import('../lib/sentry').then(({ setUserContext: set }) => set(user)).catch(() => {})
}

interface User {
  id: string
  email: string
  name: string
  subscription_tier: 'free' | 'pro' | 'max'
  email_verified: boolean
  tailored_resumes_count: number
  monthly_tailored_count: number
  monthly_count_reset_at?: string
  subscription_expires_at?: string
}

interface AuthState {
  user: User | null
  loading: boolean
  isAuthenticated: boolean
}

interface AuthContextType extends AuthState {
  login: (accessToken: string, refreshToken: string, user: User) => void
  logout: () => void
  refreshAuth: () => Promise<void>
  updateUser: (updates: Partial<User>) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Token storage keys
const ACCESS_TOKEN_KEY = 'auth_token'
const REFRESH_TOKEN_KEY = 'refresh_token'
const USER_DATA_KEY = 'user_data'

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    isAuthenticated: false,
  })

  // Helper functions for token management
  const setTokens = useCallback((accessToken: string, refreshToken: string) => {
    // Store access token in localStorage for API requests
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)

    // Store refresh token in secure httpOnly-style cookie (7 days)
    Cookies.set(REFRESH_TOKEN_KEY, refreshToken, {
      expires: 7,
      secure: true,
      sameSite: 'strict'
    })
  }, [])

  const clearTokens = useCallback(() => {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(USER_DATA_KEY)
    Cookies.remove(REFRESH_TOKEN_KEY)
  }, [])

  const getStoredRefreshToken = useCallback(() => {
    return Cookies.get(REFRESH_TOKEN_KEY)
  }, [])

  const updateUser = useCallback((updates: Partial<User>) => {
    setAuthState(prev => {
      if (!prev.user) return prev
      const updatedUser = { ...prev.user, ...updates }

      // Update localStorage
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(updatedUser))

      return {
        ...prev,
        user: updatedUser
      }
    })
  }, [])

  const login = useCallback((accessToken: string, refreshToken: string, user: User) => {
    setTokens(accessToken, refreshToken)
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(user))

    // Set user context in Sentry for error tracking
    setUserContext({ id: user.id, email: user.email })

    setAuthState({
      user,
      loading: false,
      isAuthenticated: true,
    })
  }, [setTokens])

  const logout = useCallback(() => {
    clearTokens()
    // Clear user context in Sentry
    setUserContext(null)
    setAuthState({
      user: null,
      loading: false,
      isAuthenticated: false,
    })
  }, [clearTokens])

  const refreshAuth = useCallback(async () => {
    const refreshToken = getStoredRefreshToken()

    if (!refreshToken) {
      logout()
      return
    }

    try {
      const { authAPI } = await import('../lib/api')
      const response = await authAPI.refresh(refreshToken)
      const { access_token, refresh_token, user } = response.data

      if (user) {
        login(access_token, refresh_token, user)
      } else {
        // If user data is not returned, just update tokens
        setTokens(access_token, refresh_token)
      }
    } catch (error) {
      console.error('Token refresh failed:', error)
      logout()
    }
  }, [getStoredRefreshToken, login, logout, setTokens])

  // Auto-refresh token before expiry
  useEffect(() => {
    if (!authState.isAuthenticated) return

    // Refresh token every 14 minutes (access token expires in 15 minutes)
    const interval = setInterval(refreshAuth, 14 * 60 * 1000)

    return () => clearInterval(interval)
  }, [authState.isAuthenticated, refreshAuth])

  // Listen for unauthorized events from API interceptor
  useEffect(() => {
    const handleUnauthorized = () => {
      logout()
    }

    window.addEventListener('auth:unauthorized', handleUnauthorized)
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized)
  }, [logout])

  // Initialize auth state from storage on app load
  useEffect(() => {
    const initializeAuth = async () => {
      const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY)
      const userData = localStorage.getItem(USER_DATA_KEY)
      const refreshToken = getStoredRefreshToken()

      if (accessToken && userData && refreshToken) {
        try {
          const user = JSON.parse(userData)
          setAuthState({
            user,
            loading: false,
            isAuthenticated: true,
          })

          // Try to refresh the token to ensure it's still valid
          await refreshAuth()
        } catch (error) {
          console.error('Failed to parse stored user data:', error)
          logout()
        }
      } else if (refreshToken) {
        // We have a refresh token but no access token, try to refresh
        await refreshAuth()
      } else {
        // No tokens found, user needs to login
        setAuthState(prev => ({ ...prev, loading: false }))
      }
    }

    initializeAuth()
  }, [refreshAuth, logout, getStoredRefreshToken])

  const contextValue: AuthContextType = {
    ...authState,
    login,
    logout,
    refreshAuth,
    updateUser,
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}